import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "@/lib/router-compat";
import { homeMetadata, localizedPublicPath } from "@/lib/publicLocale";
import { storeGuestSheet } from "@/lib/guestSheet";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";
import { trackInputTabSelect, trackSheetGenStart, trackSheetGenComplete, trackSheetGenError, trackSignupIntent, trackProGateHit, trackLandingCTA, initScrollTracker } from "@/lib/analytics";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Type,
  Link as LinkIcon,
  Loader2,
  ArrowRight,
  Files,
  FolderPlus,
  ChevronDown,
} from "lucide-react";
import { SparklesIcon, FolderIcon, YoutubeIcon, LockIcon, BrainIcon } from "@/components/icons/CustomIcons";
import { toast } from "sonner";
import { BookOpen } from "lucide-react";
import { FolderCreateDialog } from "@/components/sheets/FolderCreateDialog";
import { DiagnosticPrompt } from "@/components/revision/DiagnosticPrompt";
import { DiagnosticTest, type LearnerLevel } from "@/components/revision/DiagnosticTest";
import { type MCQQuestion, shuffleAllQuestions } from "@/components/revision/MCQQuiz";
import { useLearningProfile } from "@/hooks/useLearningProfile";
import { canGuestGenerate, incrementGuestUsage } from "@/lib/guestUsage";
import { GenerationError, classifyGenerationResponse, isRetryable, type GenerationErrorKind } from "@/lib/generationErrors";
import { LearningProfileSetup } from "@/components/revision/LearningProfileSetup";
import { OnboardingWelcome } from "@/components/OnboardingWelcome";
import { useSubscription } from "@/hooks/useSubscription";
import { ProGate } from "@/components/ProGate";

// Import LandingSection directly. it's above the fold and critical for initial render (avoids CLS from Suspense fallback swap)
import LandingSection from "@/components/landing/LandingSection";
import { LandingInputBar } from "@/components/landing/LandingInputBar";
import { LandingHero } from "@/components/landing/LandingHero";
// Lazy-load below-fold landing page components
const AnimatedUploadZone = lazy(() => import("@/components/landing/AnimatedUploadZone").then(m => ({ default: m.AnimatedUploadZone })));
const MagneticButton = lazy(() => import("@/components/landing/MagneticButton").then(m => ({ default: m.MagneticButton })));


import { MultiFileUpload } from "@/components/sheets/MultiFileUpload";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { FeaturesGrid } from "@/components/landing/FeaturesGrid";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { LandingFAQ } from "@/components/landing/LandingFAQ";
import { SocialProofStats } from "@/components/landing/SocialProofStats";
import { SoftwareApplicationJsonLd } from "@/components/landing/SoftwareApplicationJsonLd";
import { SectionDivider } from "@/components/landing/SectionDivider";

type InputType = "files" | "text" | "url" | "youtube";

interface Folder {
  id: string;
  name: string;
}

const SAMPLE_TEXT = `Chapter 3: Cell Biology – Structure and Function

The cell is the basic structural and functional unit of all living organisms. Cells can be broadly classified into two categories: prokaryotic and eukaryotic.

1. Prokaryotic Cells
Prokaryotic cells lack a membrane-bound nucleus. Their genetic material (DNA) is located in a region called the nucleoid. Key features include:
- Cell wall (peptidoglycan in bacteria)
- Plasma membrane
- Ribosomes (70S)
- Flagella for motility
- No membrane-bound organelles

Examples: Escherichia coli, Streptococcus pneumoniae.

2. Eukaryotic Cells
Eukaryotic cells have a true nucleus enclosed by a nuclear envelope. They contain membrane-bound organelles:

a) Nucleus: Contains chromatin (DNA + histone proteins). The nucleolus produces ribosomal RNA (rRNA).

b) Mitochondria: "Powerhouse of the cell." Site of aerobic respiration and ATP production via oxidative phosphorylation. Have their own DNA (supports endosymbiotic theory).

c) Endoplasmic Reticulum (ER):
   - Rough ER: Studded with ribosomes → protein synthesis and modification
   - Smooth ER: Lipid synthesis, detoxification, calcium storage

d) Golgi Apparatus: Modifies, packages, and ships proteins. Cis face (receiving) → trans face (shipping).

e) Lysosomes: Contain hydrolytic enzymes (pH ~5). Responsible for intracellular digestion and autophagy.

f) Cytoskeleton: Network of microfilaments (actin), intermediate filaments, and microtubules (tubulin). Provides structural support and enables cell movement.

3. Cell Membrane
The fluid mosaic model (Singer & Nicolson, 1972) describes the plasma membrane as a phospholipid bilayer with embedded proteins:
- Integral proteins: span the membrane (channels, carriers)
- Peripheral proteins: attached to surface
- Cholesterol: modulates fluidity
- Glycoproteins/glycolipids: cell recognition

Transport across the membrane:
- Passive transport: diffusion, osmosis, facilitated diffusion (no ATP required)
- Active transport: requires ATP (e.g., Na⁺/K⁺ pump: 3 Na⁺ out, 2 K⁺ in)
- Endocytosis (phagocytosis, pinocytosis) and exocytosis

4. Cell Division
- Mitosis: produces 2 identical diploid daughter cells (growth/repair)
  Phases: Prophase → Metaphase → Anaphase → Telophase
- Meiosis: produces 4 haploid gametes (sexual reproduction)
  Two divisions: Meiosis I (homologous separation) and Meiosis II (sister chromatid separation)
  Crossing over occurs in Prophase I → genetic diversity

Key formula: DNA replication follows semi-conservative model (Meselson-Stahl experiment, 1958).`;


/** Helper: call an edge function with optional auth */
async function callEdgeFunction(
  functionName: string,
  body: Record<string, unknown>,
  session: { access_token: string } | null
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "apikey": import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'],
  };
  if (session) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }
  return fetch(
    `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/${functionName}`,
    { method: "POST", headers, body: JSON.stringify(body) }
  );
}

/** Read file as base64 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix
      const base64 = result.split(",")[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NewContentPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const { profile, needsOnboarding, createProfile, incrementSheetsCreated } = useLearningProfile();
  const { subscribed } = useSubscription();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [dailySheetCount, setDailySheetCount] = useState<number | null>(null);
  const FREE_DAILY_LIMIT = 2;
  const publicHome = location.pathname === "/" || /^\/fr\/?$/.test(location.pathname);
  useSEO(publicHome ? homeMetadata(location.pathname.startsWith("/fr") ? "fr" : "en") : user ? SEO_CONFIGS.newContent : SEO_CONFIGS.home);

  const [inputType, setInputType] = useState<InputType>("files");
  const [textContent, setTextContent] = useState("");
  const [urlContent, setUrlContent] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);

  const [diagnosticPhase, setDiagnosticPhase] = useState<"none" | "prompt" | "loading" | "test" | "generating">("none");
  const [diagnosticQuestions, setDiagnosticQuestions] = useState<MCQQuestion[]>([]);
  const [extractedContent, setExtractedContent] = useState("");
  const [extractedTitle, setExtractedTitle] = useState("");
  const [extractedSourceType, setExtractedSourceType] = useState<"pdf" | "text" | "url">("pdf");
  // Refs to mirror extracted values for synchronous access (avoids stale state in async flows)
  const extractedContentRef = useRef("");
  const extractedTitleRef = useRef("");
  const extractedSourceTypeRef = useRef<"pdf" | "text" | "url">("pdf");
  
  const isLandingPage = !user;

  useEffect(() => {
    const skeleton = document.getElementById("skeleton-root");
    if (skeleton) skeleton.classList.add("hidden");
  }, []);

  // Scroll depth tracking for landing page
  useEffect(() => {
    if (!isLandingPage) return;
    return initScrollTracker("landing");
  }, [isLandingPage]);

  // Fetch daily sheet count for free users
  useEffect(() => {
    if (!user || subscribed) return;
    const today = new Date().toISOString().split("T")[0];
    supabase
      .from("revision_sheets")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .gte("created_at", `${today}T00:00:00.000Z`)
      .then(({ count }) => setDailySheetCount(count ?? 0));
  }, [user, subscribed]);

  useEffect(() => {
    if (user) fetchFolders();
  }, [user]);

  const fetchFolders = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("revision_folders")
      .select("id, name")
      .eq("user_id", user.id)
      .order("name");
    if (data) setFolders(data);
  };

  const getSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  };

  const handleLandingSubmit = (query: string, attachments: { type: string; file?: File; label: string }[]) => {
    const fileAttachments = attachments.filter(a => a.type === "file" && a.file);
    
    // Determine resolved values synchronously
    let resolvedInputType = inputType;
    let resolvedFiles = selectedFiles;
    let resolvedText = textContent;
    let resolvedUrl = urlContent;
    let resolvedYoutube = youtubeUrl;

    if (fileAttachments.length > 0) {
      resolvedFiles = fileAttachments.map(a => a.file!);
      resolvedInputType = "files";
      setSelectedFiles(resolvedFiles);
      setInputType("files");
    } else if (query.trim()) {
      const ytRegex = /(?:youtube\.com\/watch|youtu\.be\/)/i;
      const urlRegex = /^https?:\/\//i;
      if (ytRegex.test(query.trim())) {
        resolvedYoutube = query.trim();
        resolvedInputType = "youtube";
        setYoutubeUrl(resolvedYoutube);
        setInputType("youtube");
      } else if (urlRegex.test(query.trim())) {
        resolvedUrl = query.trim();
        resolvedInputType = "url";
        setUrlContent(resolvedUrl);
        setInputType("url");
      } else {
        resolvedText = query.trim();
        resolvedInputType = "text";
        setTextContent(resolvedText);
        setInputType("text");
      }
    }
    
    // Pass resolved values directly to avoid stale state
    handleTransform(resolvedInputType, resolvedFiles, resolvedText, resolvedUrl, resolvedYoutube);
  };

  const showGenerationError = (kind: GenerationErrorKind, retry?: () => void) => {
    const message = t(`generationErrors.${kind}`);
    let action: { label: string; onClick: () => void } | undefined;
    if (kind === "daily_limit" || kind === "pro_required") {
      action = { label: t("generationErrors.seePlans"), onClick: () => navigate(localizedPublicPath("/pricing", i18n.language)) };
    } else if (kind === "guest_limit") {
      action = { label: t("generationErrors.signUp"), onClick: () => navigate("/sign-in") };
    } else if (kind === "session_expired") {
      action = { label: t("generationErrors.signIn"), onClick: () => navigate("/sign-in") };
    } else if (retry && isRetryable(kind)) {
      action = { label: t("common.retry", "Retry"), onClick: retry };
    }
    toast.error(message, { duration: 8000, action });
  };

  const handleTransform = async (
    overrideInputType?: string,
    overrideFiles?: File[],
    overrideText?: string,
    overrideUrl?: string,
    overrideYoutube?: string,
  ) => {
    const effectiveInputType = overrideInputType || inputType;
    const effectiveFiles = overrideFiles || selectedFiles;
    const effectiveText = overrideText || textContent;
    const effectiveUrl = overrideUrl || urlContent;
    const effectiveYoutube = overrideYoutube || youtubeUrl;

    // Guest limit: 1 sheet without account
    if (!user) {
      if (!canGuestGenerate()) {
        toast.error(t("guest.noSheetsLeft", "No free sheets left. Sign up for unlimited access."));
        navigate("/sign-in");
        return;
      }
    }

    // Check daily sheet limit for free users
    if (user && !subscribed) {
      const today = new Date().toISOString().split("T")[0];
      const { count } = await supabase
        .from("revision_sheets")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .gte("created_at", `${today}T00:00:00.000Z`);
      
      if ((count ?? 0) >= FREE_DAILY_LIMIT) {
        trackProGateHit("daily_sheet_limit");
        showGenerationError("daily_limit");
        return;
      }
      setDailySheetCount(count ?? 0);
    }

    let combinedContent = "";
    let sourceType: "pdf" | "text" | "url" = effectiveInputType === "files" ? "pdf" : effectiveInputType === "youtube" ? "url" : effectiveInputType as "text" | "url";
    let title = "";
    const session = await getSession();

    if (effectiveInputType === "files" && effectiveFiles.length > 0) {
      setIsProcessing(true);
      setProgress(5);
      setProgressMessage(t("contentInput.extractingText", "Extracting text from documents..."));

      // Parse all files in parallel for speed
      setProgressMessage(t("contentInput.extractingText", "Extracting text from documents..."));

      const parseFile = async (file: File): Promise<{ content: string; fileName: string } | null> => {
        let content = "";

        if (user && session) {
          const filePath = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`;
          const { error: uploadError } = await supabase.storage
            .from("revision-materials")
            .upload(filePath, file);

          if (uploadError) {
            toast.error(t("contentInput.uploadFailed", "Failed to upload {{name}}", { name: file.name }));
            return null;
          }

          const { data: parseData, error: parseError } = await supabase.functions.invoke(
            "parse-material",
            { body: { filePath, returnContent: true } }
          );
          if (parseError) {
            const status = (parseError as { context?: Response }).context?.status;
            if (status === 429) throw new Error("__RATE_LIMIT__");
            console.error("parse-material error:", parseError);
            toast.error(t("generationErrors.file", { name: file.name }));
            return null;
          }
          content = parseData?.content || "";
        } else {
          try {
            const base64 = await fileToBase64(file);
            const response = await callEdgeFunction("parse-material", {
              fileBase64: base64,
              fileName: file.name,
            }, null);

            if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              if (response.status === 429) throw new Error("__RATE_LIMIT__");
              throw new Error(err.error || "Parse failed");
            }
            const parseData = await response.json();
            content = parseData?.content || "";
          } catch (err) {
            if (err instanceof Error && err.message === "__RATE_LIMIT__") throw err;
            console.error("parse-material error:", err);
            toast.error(t("generationErrors.file", { name: file.name }));
            return null;
          }
        }

        const normalizedContent = content.trim();
        const controlCharCount = normalizedContent.match(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g)?.length || 0;
        const isInvalidContent =
          !normalizedContent ||
          normalizedContent.includes("could not be extracted") ||
          normalizedContent.includes("may require OCR") ||
          (normalizedContent.length >= 100 && controlCharCount > normalizedContent.length * 0.15);

        if (isInvalidContent) {
          toast.error(t("generationErrors.file", { name: file.name }));
          return null;
        }

        return { content: `\n\n--- ${file.name} ---\n\n${normalizedContent}`, fileName: file.name.replace(/\.[^/.]+$/, "") };
      };

      try {
        const results = await Promise.all(effectiveFiles.map(parseFile));
        setProgress(45);

        const validResults = results.filter((r): r is { content: string; fileName: string } => r !== null);

        if (validResults.length === 0) {
          toast.error(t("contentInput.noValidFiles", "No valid content could be extracted"));
          setIsProcessing(false);
          return;
        }

        combinedContent = validResults.map(r => r.content).join("\n");
        const firstResult = validResults[0];
        title = validResults.length === 1 ? (firstResult?.fileName ?? "Untitled") : `${firstResult?.fileName ?? "Untitled"} + ${validResults.length - 1} more`;
      } catch (err) {
        if (err instanceof Error && err.message === "__RATE_LIMIT__") {
          setIsProcessing(false);
          setProgress(0);
          setProgressMessage("");
          showGenerationError(user ? "rate_limit" : "guest_limit");
          return;
        }
        throw err;
      }
    } else if (effectiveInputType === "text" && effectiveText.trim()) {
      combinedContent = effectiveText.trim();
      title = combinedContent.slice(0, 50).split("\n")[0] || "Untitled";
    } else if (effectiveInputType === "url" && effectiveUrl.trim()) {
      // URL extraction is not available: stop instead of generating a sheet from the bare link.
      toast.info(t("contentInput.urlNotYetSupported", "URL scraping coming soon."));
      return;
    } else if (effectiveInputType === "youtube" && effectiveYoutube.trim()) {
      if (!session) {
        toast.info(t("auth.signInForYoutube", "Sign in to use YouTube transcription"));
        navigate("/sign-in");
        return;
      }
      setIsProcessing(true);
      setProgress(10);
      setProgressMessage(t("contentInput.fetchingTranscript", "Fetching YouTube transcript..."));

      try {
        const ytResponse = await callEdgeFunction("youtube-transcript", {
          url: effectiveYoutube.trim(),
        }, session);

        if (!ytResponse.ok) {
          const errData = await ytResponse.json().catch(() => null);
          const kind = classifyGenerationResponse(ytResponse.status, errData, false);
          throw new GenerationError(kind, errData?.error);
        }

        const ytData = await ytResponse.json();
        if (!ytData?.transcript || String(ytData.transcript).trim().length < 50) {
          throw new GenerationError("generic", "empty transcript");
        }
        combinedContent = ytData.transcript;
        title = ytData.title || "YouTube Video";
      } catch (error) {
        console.error("YouTube transcript error:", error);
        if (error instanceof GenerationError && error.kind !== "generic") {
          showGenerationError(error.kind);
        } else {
          toast.error(t("generationErrors.youtube"));
        }
        setIsProcessing(false);
        setProgress(0);
        setProgressMessage("");
        return;
      }
    } else {
      toast.error(t("contentInput.noContent", "Please provide content to transform"));
      return;
    }

    setExtractedContent(combinedContent);
    setExtractedTitle(title);
    setExtractedSourceType(sourceType);
    extractedContentRef.current = combinedContent;
    extractedTitleRef.current = title;
    extractedSourceTypeRef.current = sourceType;
    setIsProcessing(false);
    setProgress(0);
    setProgressMessage("");
    // Guests skip diagnostic and go straight to standard generation
    if (!user) {
      setDiagnosticPhase("generating");
      handleGenerateSheet("standard");
      return;
    }
    setDiagnosticPhase("prompt");

    // Auto-scroll to diagnostic section after a short delay
    setTimeout(() => {
      const diagnosticEl = document.getElementById("diagnostic-section");
      if (diagnosticEl) {
        diagnosticEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const handleStartDiagnostic = async () => {
    setDiagnosticPhase("loading");
    const session = await getSession();

    try {
      const response = await callEdgeFunction("revision-ai", {
        type: "quiz",
        courseName: extractedTitle,
        content: extractedContent.slice(0, 80000),
        questionCount: 10,
      }, session);

      if (!response.ok) throw new Error("Failed to generate diagnostic");

      const data = await response.json();
      const questions: MCQQuestion[] = data.questions || [];

      if (questions.length === 0) {
        toast.error(t("diagnostic.noQuestions", "Could not generate diagnostic questions. Generating standard sheet."));
        handleGenerateSheet("standard");
        return;
      }

      setDiagnosticQuestions(shuffleAllQuestions(questions));
      setDiagnosticPhase("test");
    } catch (error) {
      console.error("Diagnostic error:", error);
      toast.error(t("diagnostic.failed", "Diagnostic failed. Generating standard sheet."));
      handleGenerateSheet("standard");
    }
  };

  const handleDiagnosticComplete = (level: LearnerLevel) => {
    handleGenerateSheet(level);
  };

  const handleSkipDiagnostic = () => {
    handleGenerateSheet("standard");
  };

  const generatingRef = useRef(false);

  const handleGenerateSheet = async (learnerLevel: string) => {
    // Prevent double calls
    if (generatingRef.current) return;
    generatingRef.current = true;
    const genStartTime = Date.now();
    let currentTitle = extractedTitleRef.current;
    const currentContent = extractedContentRef.current;
    const currentSourceType = extractedSourceTypeRef.current;
    trackSheetGenStart(currentSourceType, !!selectedFolderId);

    setDiagnosticPhase("generating");
    setIsProcessing(true);
    setProgress(55);
    setProgressMessage(t("contentInput.aiStructuring", "AI is structuring your content..."));

    const session = await getSession();
    let generatedContent = "";

    try {
      const response = await callEdgeFunction("revision-ai", {
        type: "revision",
        courseName: currentTitle,
        content: currentContent.slice(0, 80000),
        detailLevel: profile?.preferred_depth === "concise" ? "quick" : profile?.preferred_depth === "detailed" ? "comprehensive" : "standard",
        learnerLevel: learnerLevel === "standard" ? undefined : learnerLevel,
        learningProfile: profile ? {
          preferred_style: profile.preferred_style,
          preferred_depth: profile.preferred_depth,
          difficulty_preference: profile.difficulty_preference,
          weakest_topics: profile.weakest_topics,
        } : undefined,
      }, session);

      if (!response.ok) {
        const errBody = await response.json().catch(() => null);
        console.error("revision-ai error:", response.status, errBody);
        throw new GenerationError(classifyGenerationResponse(response.status, errBody, !user));
      }

      setProgress(70);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const chunkContent = parsed.choices?.[0]?.delta?.content;
            if (chunkContent) generatedContent += chunkContent;
          } catch {}
        }
      }

      if (!generatedContent || generatedContent.trim().length < 50) {
        console.error("Generated content too short:", generatedContent.length);
        throw new Error("AI returned insufficient content");
      }

      // Drop any conversational preamble before the sheet title and
      // name pasted-text sheets after their generated title.
      const titleMatch = generatedContent.match(/^#\s+(.+)$/m);
      if (titleMatch && titleMatch.index !== undefined && titleMatch.index > 0) {
        generatedContent = generatedContent.slice(titleMatch.index);
      }
      if (titleMatch && currentSourceType === "text") {
        currentTitle = (titleMatch[1] ?? "").trim().slice(0, 120) || currentTitle;
      }

      setProgress(85);
      setProgressMessage(t("contentInput.almostDone", "Almost done. saving your sheet..."));

      if (user) {
        // Refresh session before DB insert (token may have expired during streaming)
        const freshSession = await getSession();
        if (!freshSession) {
          throw new Error("Session expired. Please sign in again.");
        }

        const contentLines = generatedContent.split("\n").filter((l) => l.trim());
        const cognitiveObjective =
          contentLines.find((l) => l.toLowerCase().includes("objective"))?.replace(/^#+\s*/, "") || null;

        // Generate ID client-side to avoid PostgREST select-after-insert issues
        const sheetId = crypto.randomUUID();

        const { error: dbError } = await supabase
          .from("revision_sheets")
          .insert({
            id: sheetId,
            user_id: user.id,
            content: generatedContent,
            title: currentTitle,
            cognitive_objective: cognitiveObjective,
            source_type: currentSourceType,
            source_content: currentContent.slice(0, 10000),
            understanding_status: "new",
            folder_id: selectedFolderId || null,
            learner_level: learnerLevel,
          } as any);

        if (dbError) {
          console.error("DB insert error:", dbError);
          throw new Error(dbError.message || "Failed to save revision sheet");
        }

        trackSheetGenComplete(currentSourceType, Date.now() - genStartTime);
        setProgress(100);
        toast.success(t("contentInput.success", "Revision sheet created!"), {
          action: {
            label: t("contentInput.viewSheet", "View sheet"),
            onClick: () => navigate(`/sheet/${sheetId}`),
          },
        });
        incrementSheetsCreated();
        // Immediately increment the local daily sheet count so the UI is accurate
        setDailySheetCount(prev => (prev ?? 0) + 1);

        if (needsOnboarding) {
          setShowOnboarding(true);
        }
        navigate(`/sheet/${sheetId}`);
      } else {
        // Guest: save to sessionStorage and navigate to guest sheet view
        const contentLines = generatedContent.split("\n").filter((l) => l.trim());
        const cognitiveObjective =
          contentLines.find((l) => l.toLowerCase().includes("objective"))?.replace(/^#+\s*/, "") || null;

        storeGuestSheet(({
          title: currentTitle,
          content: generatedContent,
          cognitive_objective: cognitiveObjective,
          source_content: currentContent.slice(0, 10000),
          learner_level: learnerLevel,
          created_at: new Date().toISOString(),
        }));

        incrementGuestUsage();
        trackSheetGenComplete(currentSourceType, Date.now() - genStartTime);
        setProgress(100);
        navigate("/sheet/guest");
      }
    } catch (error) {
      console.error("Error generating sheet:", error);
      trackSheetGenError(currentSourceType, (error as Error).message || "unknown");
      if (error instanceof GenerationError && error.kind !== "generic") {
        if (error.kind === "daily_limit") trackProGateHit("daily_sheet_limit");
        showGenerationError(error.kind, () => handleGenerateSheet(learnerLevel));
      } else {
        toast.error(t("contentInput.generateFailed", "Failed to generate revision sheet"), {
          action: {
            label: t("common.retry", "Retry"),
            onClick: () => handleGenerateSheet(learnerLevel),
          },
        });
      }
    } finally {
      generatingRef.current = false;
      setIsProcessing(false);
      setProgress(0);
      setProgressMessage("");
      setDiagnosticPhase("none");
    }
  };

  const [sampleTriggered, setSampleTriggered] = useState(false);

  const handleTrySample = () => {
    setInputType("text");
    setTextContent(SAMPLE_TEXT);
    setSampleTriggered(true);
  };

  // Auto-trigger transform when sample is loaded
  useEffect(() => {
    if (sampleTriggered && textContent === SAMPLE_TEXT && inputType === "text") {
      setSampleTriggered(false);
      handleTransform();
    }
  }, [sampleTriggered, textContent, inputType]);

  // Override canTransform check: also account for sample being loaded
  const canTransform =
    (inputType === "files" && selectedFiles.length > 0) ||
    (inputType === "text" && textContent.trim()) ||
    (inputType === "url" && urlContent.trim()) ||
    (inputType === "youtube" && youtubeUrl.trim());

  // No authLoading skeleton gate. render landing page immediately to prevent CLS.
  // During auth loading, user is null so isLandingPage=true, which shows the landing page directly.

  return (
    <div className="flex flex-col flex-1">
      {isLandingPage && (
        <LandingSection onSubmit={handleLandingSubmit} isLoading={isProcessing} onTrySample={handleTrySample} />
      )}

      <div className={`container mx-auto px-4 sm:px-6 py-6 relative flex flex-col ${isLandingPage ? 'max-w-2xl pb-0' : 'max-w-3xl flex-1'}`}>
        {/* Free usage banner for guests */}
        {/* Free usage info removed for cleaner landing */}

        {!isLandingPage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center flex-1"
          >
            <div className="w-full space-y-6">
              {/* Daily usage counter for free users */}
              {user && !subscribed && dailySheetCount !== null && (
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border/60 bg-secondary/50 text-sm">
                    <SparklesIcon className="w-4 h-4 text-primary" />
                    <span className={dailySheetCount >= FREE_DAILY_LIMIT ? "text-destructive font-medium" : "text-muted-foreground"}>
                      {dailySheetCount >= FREE_DAILY_LIMIT
                        ? t("pro.dailyLimitReachedShort", "{{used}}/{{max}}. daily limit reached", { used: dailySheetCount, max: FREE_DAILY_LIMIT })
                        : t("pro.dailyCounter", "{{remaining}} of {{max}} sheets remaining today", { remaining: FREE_DAILY_LIMIT - dailySheetCount, max: FREE_DAILY_LIMIT })}
                    </span>
                  </div>
                </div>
              )}

              {/* Hero title */}
              <LandingHero />

              {/* Same compact input bar as landing */}
              <LandingInputBar onSubmit={handleLandingSubmit} isLoading={isProcessing} />

              {/* Progress indicator */}
              {isProcessing && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="max-w-2xl mx-auto space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-sm text-center text-muted-foreground">{progressMessage}</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Diagnostic Flow Overlay */}
        {diagnosticPhase !== "none" && diagnosticPhase !== "generating" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" id="diagnostic-section">
            {diagnosticPhase === "prompt" && (
              !user ? (
                <Card className="border-primary/20 shadow-lg">
                  <CardContent className="p-8 text-center space-y-4">
                    <BrainIcon className="w-12 h-12 mx-auto text-primary" />
                    <div>
                      <h2 className="text-xl font-semibold mb-2">
                        {t("diagnostic.title", "Personalize your revision sheet")}
                      </h2>
                      <p className="text-muted-foreground text-sm max-w-md mx-auto">
                        {t("guest.diagnosticSignup", "Create a free account to unlock personalized diagnostics and get 3 revision sheets per day.")}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button onClick={() => navigate("/sign-in")} className="gap-2" size="lg">
                        <SparklesIcon className="w-4 h-4" />
                        {t("guest.modal.cta", "Sign up for free")}
                      </Button>
                      <Button variant="outline" onClick={handleSkipDiagnostic} className="gap-2" size="lg">
                        {t("diagnostic.skip", "Skip. Standard level")}
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("guest.modal.note", "No credit card required. It takes 30 seconds.")}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <DiagnosticPrompt
                  onTakeDiagnostic={subscribed ? handleStartDiagnostic : () => navigate(localizedPublicPath("/pricing", i18n.language))}
                  onSkip={handleSkipDiagnostic}
                  isLoading={false}
                  proLocked={!subscribed}
                />
              )
            )}
            {diagnosticPhase === "loading" && (
              <Card className="border-primary/20 shadow-lg">
                <CardContent className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-muted-foreground">{t("diagnostic.generating", "Generating diagnostic questions...")}</p>
                </CardContent>
              </Card>
            )}
            {diagnosticPhase === "test" && diagnosticQuestions.length > 0 && (
              <DiagnosticTest questions={diagnosticQuestions} onComplete={handleDiagnosticComplete} isGeneratingSheet={isProcessing} />
            )}
          </motion.div>
        )}


        {user && diagnosticPhase === "none" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-center mt-10">
            <Button variant="ghost" className="gap-2 text-muted-foreground hover:text-foreground" onClick={() => navigate("/my-sheets")}>
              <FolderIcon className="w-4 h-4" />
              {t("contentInput.viewMySheets", "View my revision sheets")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        )}
      </div>




      {/* Landing persuasion sections removed */}

      {user && (
        <FolderCreateDialog
          open={showCreateFolder}
          onOpenChange={setShowCreateFolder}
          userId={user.id}
          onFolderCreated={(folder) => {
            setFolders([...folders, folder].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedFolderId(folder.id);
          }}
        />
      )}


      {user && (
        <OnboardingWelcome
          open={showOnboarding}
          onComplete={() => {
            setShowOnboarding(false);
            createProfile({
              preferred_style: "visual",
              preferred_depth: "standard",
              difficulty_preference: "adaptive",
            });
          }}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
